<template>
  <div>
    <gmap-map
      id="print"
      ref="googleMapRef"
      :center="mapCenter"
      :zoom="mapZoom"
      :options="{
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        mapTypeControlOptions: {
          position: 2,
        },
      }"
      map-type-id="roadmap"
      style="height: 100vh"
    >
      <gmap-marker
        :key="index"
        v-for="(marker, index) in markers"
        :position="marker.position"
        :clickable="false"
        :label="getLabel(marker.label)"
        :icon="marker.icon"
      />

      <v-sheet class="overlay-print-info pa-4 no-print">
        <v-checkbox
          class="mt-0 pt-0"
          v-model="fieldFillSetting"
          :label="fieldFillSettingText"
          @change="fieldFillSettingChange()"
        ></v-checkbox>
        <v-checkbox class="mt-0 pt-0" v-model="fieldLabelSetting" :label="fieldLabelSettingText"></v-checkbox>
        <v-radio-group v-if="isShowBackgroundSetting" v-model="backgroundStyle" column @change="setBackgroundStyle()">
          <v-radio :label="$tt('customDefault')" value="default"></v-radio>
          <v-radio :label="$tt('print.field.backgroundWhiteColorSetting')" value="whiteColor"></v-radio>
          <v-radio :label="$tt('print.field.backgroundNoColorSetting')" value="eraseBackground"></v-radio>
        </v-radio-group>
        <v-row no-gutters justify="end">
          <v-btn color="primary" @click="print()"> {{ $tt('print.field.printText') }}</v-btn>
        </v-row>
      </v-sheet>
      <v-tooltip right>
        <template v-slot:activator="{ on }">
          <v-btn fab dark @click="switchToNormal()" class="ma-4" color="grey darken-3" v-on="on">
            <v-icon large>mdi-earth</v-icon>
          </v-btn>
        </template>
        <span class="aid-logotype">AgriBus-Web</span>
      </v-tooltip>
    </gmap-map>
  </div>
</template>

<script>
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import MapMixin from '@/components/mixins/map-mixin'
import * as Constants from '@/lib/constants'
import i18n from '@/lib/i18n'

const FIELD_FILL_OPACITY = '0.5'
const FIELD_NOT_FILL_OPACITY = '0'

export default {
  name: 'FieldPrint',
  mixins: [MapMixin, GoogleMapMixin],
  data() {
    return {
      fieldFillSetting: true,
      fieldFillSettingText: i18n.tt('print.field.fillSetting'),
      fieldLabelSetting: true,
      fieldLabelSettingText: i18n.tt('print.field.showFieldName'),
      backgroundStyle: 'default',
      isShowBackgroundSetting: true,
      mapStyle: {
        default: [],
        whiteColor: [
          {
            featureType: 'landscape.natural',
            elementType: 'geometry.fill',
            stylers: [
              {
                color: '#ffffff',
              },
            ],
          },
          {
            featureType: 'landscape.natural.landcover',
            elementType: 'geometry.fill',
            stylers: [
              {
                color: '#ffffff',
              },
            ],
          },
          {
            featureType: 'landscape.natural.landcover',
            elementType: 'labels',
            stylers: [
              {
                color: '#ffffff',
              },
            ],
          },
        ],
        eraseBackground: [
          {
            stylers: [
              {
                color: '#ffffff',
              },
            ],
          },
          {
            elementType: 'labels',
            stylers: [
              {
                color: '#ffffff',
              },
            ],
          },
          {
            elementType: 'labels.icon',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
        ],
      },
    }
  },

  mounted() {
    this.$store.dispatch('setShowFieldPrint', true)
    const params = { onSuccess: this.initMap }
    this.$store.dispatch('getAllFields', params)
  },
  methods: {
    setBackgroundStyle() {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        let mapType = new google.maps.StyledMapType(this.mapStyle.default)
        switch (this.backgroundStyle) {
          case 'whiteColor':
            mapType = new google.maps.StyledMapType(this.mapStyle.whiteColor)
            map.mapTypes.set('whiteColor', mapType)
            map.setMapTypeId('whiteColor')
            break
          case 'eraseBackground':
            mapType = new google.maps.StyledMapType(this.mapStyle.eraseBackground)
            map.mapTypes.set('eraseBackground', mapType)
            map.setMapTypeId('eraseBackground')
            break
          default:
            map.setMapTypeId(google.maps.MapTypeId.ROADMAP)
        }
      })
    },
    setFieldStyle(fillOpacity) {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.data.setStyle(feature => {
          return {
            fillColor: Constants.FIELD_FILL_COLOR,
            fillOpacity: fillOpacity,
            strokeColor: Constants.FIELD_FILL_COLOR,
            strokeWeight: 1,
          }
        })
      })
    },
    fieldFillSettingChange() {
      if (this.fieldFillSetting) {
        this.setFieldStyle(FIELD_FILL_OPACITY)
      } else {
        this.setFieldStyle(FIELD_NOT_FILL_OPACITY)
      }
    },
    getLabel(label) {
      if (this.fieldLabelSetting) {
        return label
      }
      return ''
    },
    print() {
      window.print()
    },
    switchToNormal() {
      this.nowDrawer = false
      this.$store.dispatch('setNowShowingRealtime', false)
      this.$router.push({ name: 'FieldListAsList' })
    },
    initMap() {
      this.$store.dispatch('setFieldGeoJsons', { type: 'FeatureCollection', features: [] })
      this.drawAllFieldsOnGoogleMap()
    },
  },
  computed: {
    mapCenter() {
      return this.getDefaultMapCenter()
    },
    mapZoom() {
      return this.getDefaultZoomLevel()
    },
    features() {
      return this.$store.getters.fieldGeoJsons.features
    },
  },
  beforeDestroy() {
    this.$store.dispatch('setShowFieldPrint', false)
  },
  watch: {
    features(newValue) {
      if (newValue.length > 0) {
        this.$refs.googleMapRef.$mapPromise.then(map => {
          map.setTilt(0)
          map.data.addGeoJson(this.$store.getters.fieldGeoJsons)
          let bounds = new google.maps.LatLngBounds()
          map.data.forEach(feature => {
            feature.getGeometry().forEachLatLng(latlng => {
              bounds.extend(latlng)
            })
          })
          map.fitBounds(bounds)
          const vm = this
          google.maps.event.addListener(map, 'maptypeid_changed', function() {
            const maptypeid = map.getMapTypeId()
            if (maptypeid == google.maps.MapTypeId.HYBRID) {
              vm.isShowBackgroundSetting = false
            } else if (maptypeid == google.maps.MapTypeId.ROADMAP) {
              vm.isShowBackgroundSetting = true
              vm.backgroundStyle = 'default'
            } else if (maptypeid == google.maps.MapTypeId.SATELLITE) {
              vm.isShowBackgroundSetting = false
            } else if (maptypeid == google.maps.MapTypeId.TERRAIN) {
              vm.isShowBackgroundSetting = false
            }
          })
        })
        this.setFieldStyle(FIELD_FILL_OPACITY)
      }
    },
  },
}
</script>
<style>
@media print {
  .no-print {
    display: none !important;
  }
  #map_canvas div > img {
    position: absolute;
  }
  .gm-style div > img {
    position: absolute;
  }
}
.overlay-print-info .v-input--selection-controls .v-messages {
  display: none !important;
}
</style>
<style scoped>
.overlay-print-info {
  position: absolute;
  top: 100px;
  left: 18px;
  z-index: 2;
}
</style>
